import {
  IAdminPharmacyListBaseData,
  adminActions,
  adminActivePharmacyList,
  adminDeactivatedPharmacyList,
  adminDeletedPharmacyList,
  getActivePharmacyList,
  getDeactivatedPharmacyList,
  successSelector,
  appointedPharmacyList,
} from '@pharmaplan/common';
import {
  getAppointedPharmaciesList,
  getDeletedPharmacyList,
} from '@pharmaplan/common/actions/adminActions';

import {
  activePharmaciesHeader,
  activePharmaciesList,
  IPharmacyRowSelection,
  appointedPharmaciesList,
  deactivatedPharmaciesHeader,
  deactivatedPharmaciesList,
  deletedPharmaciesHeader,
  deletedPharmaciesList,
} from '../../../components/Admin/PharmacyList/helper';
import { PharmacyListTabTypes } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import useGetItems from '../../useGetItems';
import usePharmacyListActions from './usePharmacyListAction';
import useUserProfileLink from '../useUserProfileLink';

const {
  myAccounts,
  activePharmacies,
  deactivatedPharmacies,
  deletedPharmacies,
  appointedPharmacies,
} = PharmacyListTabTypes;

const usePharmacyListTabs = (
  selectedTab: PharmacyListTabTypes,
  selection?: IPharmacyRowSelection | null,
) => {
  const isAppointed = selectedTab === PharmacyListTabTypes.appointedPharmacies;

  const { actions, activateModal } = usePharmacyListActions(isAppointed);
  const { getImage } = useGetItems();
  const { goToUserProfile } = useUserProfileLink();

  const headersConfig = {
    [myAccounts]: activePharmaciesHeader(),
    [activePharmacies]: activePharmaciesHeader(!!selection),
    [deactivatedPharmacies]: deactivatedPharmaciesHeader(),
    [deletedPharmacies]: deletedPharmaciesHeader(),
    [appointedPharmacies]: activePharmaciesHeader(),
  };

  const active = useAppSelector(adminActivePharmacyList);
  const deactivated = useAppSelector(adminDeactivatedPharmacyList);
  const deleted = useAppSelector(adminDeletedPharmacyList);
  const appointed = useAppSelector(appointedPharmacyList);

  const activeListSuccess = useAppSelector((state) =>
    successSelector([adminActions.getActivePharmacyList], state));
  const deactivatedListSuccess = useAppSelector((state) =>
    successSelector([adminActions.getDeactivatedPharmacyList], state));
  const deletedSuccess = useAppSelector((state) =>
    successSelector([adminActions.getDeletedPharmacyList], state));
  const appointedSuccess = useAppSelector((state) =>
    successSelector([adminActions.appointedPharmaciesList], state));

  const dataConfig = {
    [myAccounts]: active,
    [activePharmacies]: active,
    [deactivatedPharmacies]: deactivated,
    [deletedPharmacies]: deleted,
    [appointedPharmacies]: appointed,
  };

  const loadSuccessConfig = {
    [myAccounts]: activeListSuccess,
    [activePharmacies]: activeListSuccess,
    [deactivatedPharmacies]: deactivatedListSuccess,
    [deletedPharmacies]: deletedSuccess,
    [appointedPharmacies]: appointedSuccess,
  };

  const { data } = dataConfig[selectedTab as keyof typeof dataConfig];

  const api = {
    [myAccounts]: getActivePharmacyList,
    [activePharmacies]: getActivePharmacyList,
    [deactivatedPharmacies]: getDeactivatedPharmacyList,
    [deletedPharmacies]: getDeletedPharmacyList,
    [appointedPharmacies]: getAppointedPharmaciesList,
  };

  const rowConfig = {
    [myAccounts]: activePharmaciesList(
      data as Array<IAdminPharmacyListBaseData>,
      getImage,
      goToUserProfile,
      actions,
    ),
    [activePharmacies]: activePharmaciesList(
      data as Array<IAdminPharmacyListBaseData>,
      getImage,
      goToUserProfile,
      actions,
      selection,
    ),
    [deactivatedPharmacies]: deactivatedPharmaciesList(
      data as Array<IAdminPharmacyListBaseData>,
      getImage,
      goToUserProfile,
      activateModal,
    ),
    [deletedPharmacies]: deletedPharmaciesList(
      data as Array<IAdminPharmacyListBaseData>,
      getImage,
      goToUserProfile,
    ),
    [appointedPharmacies]: appointedPharmaciesList(
      data,
      getImage,
      goToUserProfile,
      actions,
    ),
  };

  return {
    rows: rowConfig[selectedTab],
    headers: headersConfig[selectedTab],
    api: api[selectedTab],
    data: dataConfig[selectedTab],
    loadSuccess: loadSuccessConfig[selectedTab],
  };
};

export default usePharmacyListTabs;
